import { useQuery } from '@tanstack/react-query';

import { Markdown } from '@/ui/Markdown';
import { formatDate, formatUtcDate } from '@/ui/format';

import type { Release } from './releaseIndex';

function meta(release: Release, deployedAt: string | null | undefined, isRunning: boolean): string {
  if (isRunning && deployedAt) return `Running now, deployed ${formatDate(deployedAt)}`;
  return release.date ? `Released ${formatUtcDate(release.date)}` : '';
}

/** The notes of one release: its body loads on its own, one release at a time, and is kept once loaded. */
function Notes({ release, loadBody }: { release: Release; loadBody: (version: string) => Promise<string> }) {
  const body = useQuery({
    queryKey: ['release-body', release.version],
    queryFn: () => loadBody(release.version),
    staleTime: Infinity,
  });
  if (body.isPending) return <p className="hint">Loading notes…</p>;
  if (body.isError) return <p className="hint">The notes could not be loaded.</p>;
  return body.data ? <Markdown headingLevel={3}>{body.data}</Markdown> : null;
}

export function ReleaseCard({
  release,
  isRunning,
  deployedAt,
  newer,
  older,
  onSelect,
  loadBody,
}: {
  release: Release;
  isRunning: boolean;
  deployedAt: string | null | undefined;
  /** The neighbours in the full index; absent at the ends. */
  newer: Release | undefined;
  older: Release | undefined;
  onSelect: (version: string) => void;
  loadBody: (version: string) => Promise<string>;
}) {
  const line = meta(release, deployedAt, isRunning);
  return (
    <article className="card whats-new-card" aria-labelledby="release-title">
      <div className="card-body">
        <h2 id="release-title" className="whats-new-title">
          Version {release.version}
        </h2>
        {line && <p className="whats-new-meta">{line}</p>}
        {release.highlight && <p className="whats-new-highlight">{release.highlight}</p>}
        <Notes release={release} loadBody={loadBody} />
        <div className="whats-new-steps">
          <button type="button" className="btn btn-secondary" disabled={!newer} onClick={() => newer && onSelect(newer.version)}>
            Newer
          </button>
          <button type="button" className="btn btn-secondary" disabled={!older} onClick={() => older && onSelect(older.version)}>
            Older
          </button>
        </div>
      </div>
    </article>
  );
}
